import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import TrialCountdown from "./TrialCountdown";
import { LOGO_URL } from "../lib/constants";

const publicLinks = [
  { to: "/", label: "Home" },
  { to: "/categories", label: "Search by Category" },
  { to: "/ai-tools", label: "AI Tools" },
  { to: "/partners", label: "Partners" },
  { to: "/enterprise", label: "For Teams" },
  { to: "/pricing", label: "Pricing" },
  { to: "/blog", label: "Blog" },
];
const memberLinks = [
  { to: "/", label: "Home" },
  { to: "/app", label: "Search" },
  { to: "/categories", label: "By Category" },
  { to: "/training", label: "Training" },
  { to: "/ai-tools", label: "AI Tools" },
  { to: "/partners", label: "Partners" },
  { to: "/blog", label: "Blog" },
  { to: "/app/favourites", label: "Favourites" },
  { to: "/app/recent", label: "Recently Used" },
  { to: "/app/account", label: "My Account" },
];
const freeLoggedInLinks = [
  { to: "/", label: "Home" },
  { to: "/categories", label: "Search by Category" },
  { to: "/ai-tools", label: "AI Tools" },
  { to: "/partners", label: "Partners" },
  { to: "/enterprise", label: "For Teams" },
  { to: "/pricing", label: "Pricing" },
  { to: "/blog", label: "Blog" },
  { to: "/app/account", label: "My Account" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isMember = user && user.membership !== "free";
  const isPaid = user && ["student", "pro", "lifetime"].includes(user.membership);
  const links = user ? (isMember ? memberLinks : freeLoggedInLinks) : publicLinks;

  return (
    <header className="sticky top-0 z-50 bg-[#0B0F0D]/95 backdrop-blur-md border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 min-w-0" data-testid="nav-logo">
          <img src={LOGO_URL} alt="AI Shorthand" className="h-9 w-9 rounded-full object-cover shrink-0" />
          <span className="min-w-0">
            <span className="block font-display font-bold text-white text-base sm:text-lg tracking-tight leading-tight">
              AI <span className="text-[#D4AF37]">Shorthand</span>
            </span>
            <span className="hidden sm:block text-[9px] font-bold tracking-[0.14em] text-[#D4AF37]/80 uppercase leading-tight whitespace-nowrap">
              Universal Short-Command Prompt Dictionary™
            </span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${location.pathname === l.to ? "text-[#D4AF37]" : "text-gray-300 hover:text-white"}`}>
              {l.label}
            </Link>
          ))}
          {user && user.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" className="px-3.5 py-2 rounded-lg text-sm font-medium text-gray-300 hover:text-white">Admin</Link>
          )}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              {user.membership === "trial" && <TrialCountdown endsAt={user.trial_ends_at} />}
              {!isPaid && (
                <button data-testid="nav-upgrade-btn" onClick={() => navigate("/pricing")}
                  className="bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 text-sm font-bold px-4 py-2 rounded-full hover:brightness-105 transition-all">
                  {user.membership === "trial" ? "Upgrade" : "Get Access"}
                </button>
              )}
              <button data-testid="nav-logout-btn" onClick={async () => { await logout(); navigate("/"); }}
                className="text-sm text-gray-300 hover:text-white font-medium">Log out</button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-link" className="text-sm text-gray-300 hover:text-white font-medium">Login</Link>
              <button data-testid="nav-cta-btn" onClick={() => navigate("/pricing")}
                className="bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 text-sm font-bold px-4 py-2 rounded-full hover:brightness-105 transition-all">
                Get Instant Access
              </button>
            </>
          )}
        </div>
        <button className="md:hidden text-white p-2" data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)}>
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-[#0B0F0D] border-t border-white/10 px-4 pb-4" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="block py-3 text-gray-200 font-medium border-b border-white/5">{l.label}</Link>
          ))}
          {isMember && (
            <Link to="/app/cheatsheet" onClick={() => setOpen(false)} className="block py-3 text-gray-200 font-medium border-b border-white/5">Cheat Sheet</Link>
          )}
          {user && user.role === "admin" && (
            <Link to="/admin" onClick={() => setOpen(false)} className="block py-3 text-gray-200 font-medium border-b border-white/5">Admin</Link>
          )}
          {user ? (
            <button onClick={async () => { setOpen(false); await logout(); navigate("/"); }} className="block py-3 text-gray-400">Log out</button>
          ) : (
            <div className="flex gap-3 pt-3">
              <Link to="/login" onClick={() => setOpen(false)} className="flex-1 text-center py-2.5 rounded-xl border border-white/20 text-white font-medium">Login</Link>
              <Link to="/pricing" onClick={() => setOpen(false)} className="flex-1 text-center py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-bold">Get Access</Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
};
